import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiMapPin,
  FiClock,
  FiCheckCircle,
  FiCopy,
  FiCheck,
  FiMessageSquare,
  FiPhone,
  FiMail,
  FiFileText,
  FiDownload,
  FiInfo,
} from "react-icons/fi";
import MediaGallery from "../../components/media/MediaGallery";
import DOMPurify from "dompurify";
import TimeAgo from "../../components/timeAgo/TimeAgo";
import { showToast } from "../../../components/common/notification/Notification";
import styles from "./PostDetailCard.module.css";

// Helper to extract structured property attributes from text without inventing data
const parsePropertyAttributes = (text = "", city = "", state = "") => {
  if (!text) return { features: [], chips: {} };

  const cleanText = text.replace(/<[^>]+>/g, " ");
  const chips = {};
  const features = [];

  // 1. Rent / Demand / Price
  const rentMatch = cleanText.match(
    /(?:demand|rent|price|rs\.?|inr|₹)\s*[:=]?\s*(\d[\d,]*(?:\.\d+)?|\d+\s*(?:k|lakh|lac|cr))/i
  );
  if (rentMatch) {
    let val = rentMatch[1].replace(/,/g, "");
    if (!isNaN(val)) {
      chips.rent = `₹${Number(val).toLocaleString("en-IN")}`;
    } else {
      chips.rent = `₹${rentMatch[1]}`;
    }
  }

  // 2. Area / Size
  const areaMatch = cleanText.match(
    /(\d[\d,]*(?:\.\d+)?)\s*(?:sq\s*ft|sqft|sq\.ft|sq\s*yards|sq\s*m|sq\s*meter)/i
  );
  if (areaMatch) {
    chips.area = `${areaMatch[1]} sq ft`;
    features.push(`${areaMatch[1]} sq ft Area`);
  }

  // 3. Bedrooms / BHK
  const bhkMatch = cleanText.match(/(\d+)\s*(?:bhk|bedroom|bed)/i);
  if (bhkMatch) {
    chips.bedrooms = `${bhkMatch[1]} Bedrooms`;
    features.push(`${bhkMatch[1]} Bedrooms`);
  }

  // 4. Balconies
  const balconyMatch = cleanText.match(/(\d+)\s*(?:balcony|balconies)/i);
  if (balconyMatch) {
    chips.balconies = `${balconyMatch[1]} Balconies`;
    features.push(`${balconyMatch[1]} Balconies`);
  }

  // 5. Furnishing
  const furnishMatch = cleanText.match(
    /\b(semi[- ]furnished|fully[- ]furnished|unfurnished)\b/i
  );
  if (furnishMatch) {
    const fTitle = furnishMatch[1].toLowerCase().includes("semi")
      ? "Semi Furnished"
      : furnishMatch[1].toLowerCase().includes("fully")
      ? "Fully Furnished"
      : "Unfurnished";
    chips.furnishing = fTitle;
    features.push(fTitle);
  }

  // 6. Maintenance
  const maintMatch = cleanText.match(/maintenance\s+([a-zA-Z0-9]+)/i);
  if (maintMatch) {
    const val = maintMatch[1];
    chips.maintenance = val.charAt(0).toUpperCase() + val.slice(1);
  }

  // 7. Extra features
  if (/modular\s+kitchen/i.test(cleanText)) {
    features.push("Modular Kitchen");
  }
  if (/wardrobe[s]?/i.test(cleanText)) {
    features.push("Wardrobes");
  }
  if (
    /car\s+parking|parking/i.test(cleanText) &&
    !features.includes("Dedicated Parking")
  ) {
    features.push("Dedicated Parking");
  }
  if (/power\s*backup/i.test(cleanText)) {
    features.push("Power Backup");
  }
  if (/swimming\s*pool/i.test(cleanText)) {
    features.push("Swimming Pool");
  }
  if (/gym|clubhouse/i.test(cleanText)) {
    features.push("Clubhouse & Gym");
  }

  return { chips, features };
};

const PostDetailCard = ({
  creatorName = "User",
  description = "",
  createdOn,
  state,
  city,
  images = [],
  videos = [],
  attachments = [],
  service,
  photo,
  budget,
  propertyType,
  areaName,
  mobileNumber,
  emailAddress,
  userType,
  postId,
  back = "/partner/posts",
  discussionPath,
}) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  const rawAssetUrl =
    process.env.REACT_APP_ASSET_URL || process.env.REACT_APP_BASE_URL || "";
  const cleanAssetUrl = rawAssetUrl ? rawAssetUrl.replace(/\/+$/, "") : "";

  const fullPhotoSrc = photo
    ? photo.startsWith("http://") || photo.startsWith("https://")
      ? photo
      : `${cleanAssetUrl}${photo.startsWith("/") ? "" : "/"}${photo}`
    : null;

  const initials = (creatorName || "User")
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");

  const locationString = [areaName, city, state].filter(Boolean).join(", ");

  // Formatted budget
  const formattedBudget =
    budget !== undefined && budget !== null && budget !== ""
      ? !isNaN(Number(budget))
        ? `₹${Number(budget).toLocaleString("en-IN")}`
        : String(budget).startsWith("₹")
        ? budget
        : `₹${budget}`
      : null;

  // Structured attributes
  const { chips, features } = useMemo(
    () => parsePropertyAttributes(description, city, state),
    [description, city, state]
  );

  const handleCopyLink = () => {
    try {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      showToast("Link copied to clipboard!", { type: "success" });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      showToast("Unable to copy link", { type: "error" });
    }
  };

  const hasMedia = images.length > 0 || videos.length > 0;
  const postHeading = service
    ? `${service} Requirement`
    : postId
    ? `Requirement Post #${postId}`
    : "Requirement Post Details";

  return (
    <div className={styles.wrapper}>
      {/* 1. Page Header with Back Navigation and Actions */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <button
            type="button"
            className={styles.headerBackIconBtn}
            onClick={() => navigate(back)}
            aria-label="Back to shared posts"
            title="Back to shared posts"
          >
            <FiArrowLeft size={16} />
            <span>Back</span>
          </button>
          <div className={styles.headerTitles}>
            <span className={styles.pageCategory}>Shared Post Details</span>
            <h1 className={styles.pageTitle}>Requirement Specs</h1>
          </div>
        </div>

        <div className={styles.pageHeaderActions}>
          {discussionPath && (
            <button
              type="button"
              className={styles.discussionActionBtn}
              onClick={() => navigate(discussionPath)}
              title="Open Discussion & Responses"
            >
              <FiMessageSquare size={15} />
              <span>View Discussion</span>
            </button>
          )}

          <button
            type="button"
            className={styles.secondaryActionBtn}
            onClick={handleCopyLink}
            title="Copy post link"
          >
            {copied ? (
              <FiCheck size={14} className="text-success" />
            ) : (
              <FiCopy size={14} />
            )}
            <span>{copied ? "Copied" : "Share"}</span>
          </button>
        </div>
      </div>

      {/* 2. Main Two-Column Layout */}
      <div className={styles.mainCard}>
        {/* Left Column: Media, Details, Specs, Description, Attachments */}
        <div className={styles.contentCol}>
          {/* Media Gallery */}
          {hasMedia && (
            <div className={styles.mediaSection}>
              <MediaGallery
                images={images}
                promotion_videos={videos}
                attachments={[]}
              />
            </div>
          )}

          {/* Overview Info Block */}
          <div className={styles.overviewHeader}>
            <h2 className={styles.postHeading}>{postHeading}</h2>

            {locationString && (
              <div className={styles.locationRow}>
                <FiMapPin className={styles.locationIcon} />
                <span>{locationString}</span>
              </div>
            )}

            <div className={styles.priceAndTagsRow}>
              {(formattedBudget || chips.rent) && (
                <div className={styles.priceValue}>
                  {formattedBudget || chips.rent}
                </div>
              )}

              <div className={styles.tagPills}>
                {service && (
                  <span className={styles.propertyTag}>{service}</span>
                )}
                {propertyType && (
                  <span className={styles.pillBadge}>{propertyType}</span>
                )}
                {chips.area && (
                  <span className={styles.pillBadge}>{chips.area}</span>
                )}
                {chips.bedrooms && (
                  <span className={styles.pillBadge}>{chips.bedrooms}</span>
                )}
              </div>
            </div>
          </div>

          {/* Key Attributes Grid */}
          {(formattedBudget ||
            propertyType ||
            chips.area ||
            chips.bedrooms ||
            chips.furnishing ||
            city ||
            state) && (
            <div className={styles.attributesGrid}>
              {formattedBudget && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Expected Budget</span>
                  <span className={styles.attrVal}>{formattedBudget}</span>
                </div>
              )}
              {propertyType && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Property Type</span>
                  <span className={styles.attrVal}>{propertyType}</span>
                </div>
              )}
              {chips.area && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Preferred Area</span>
                  <span className={styles.attrVal}>{chips.area}</span>
                </div>
              )}
              {chips.bedrooms && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Configuration</span>
                  <span className={styles.attrVal}>{chips.bedrooms}</span>
                </div>
              )}
              {chips.furnishing && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Furnishing</span>
                  <span className={styles.attrVal}>{chips.furnishing}</span>
                </div>
              )}
              {(city || state) && (
                <div className={styles.attributeItem}>
                  <span className={styles.attrLabel}>Location</span>
                  <span className={styles.attrVal}>
                    {[city, state].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Post Description Section */}
          {description && (
            <div className={styles.detailsSection}>
              <h3 className={styles.sectionHeading}>Requirement Details</h3>
              <div
                className={styles.descriptionBox}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description),
                }}
              />

              {/* Key Features (if extracted from description) */}
              {features.length > 0 && (
                <div className={styles.featuresGrid}>
                  {features.map((feat, idx) => (
                    <span key={idx} className={styles.featureTag}>
                      <FiCheckCircle className={styles.featureIcon} />
                      {feat}
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Attachments / Documents */}
          {attachments && attachments.length > 0 && (
            <div className={styles.attachmentsSection}>
              <h3 className={styles.sectionHeading}>
                Attached Documents ({attachments.length})
              </h3>
              <div className={styles.attachmentsList}>
                {attachments.map((att, idx) => {
                  const url = att.attachment_url || att.url || att.url_path;
                  const name = att.name || `Attachment_${idx + 1}.pdf`;
                  return (
                    <a
                      key={idx}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.attachmentCard}
                    >
                      <div className={styles.attachmentLeft}>
                        <FiFileText size={18} className="text-primary" />
                        <span>{name}</span>
                      </div>
                      <FiDownload size={14} className="text-muted" />
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Right Column: Poster Summary Card & Quick Discussion Jump */}
        <div className={styles.sidebarCol}>
          {/* Poster Profile Card */}
          <div className={styles.posterCard}>
            <div className={styles.posterCardHeader}>
              {fullPhotoSrc ? (
                <img
                  src={fullPhotoSrc}
                  alt={creatorName}
                  className={styles.posterAvatar}
                  onError={(e) => {
                    e.target.style.display = "none";
                    if (e.target.nextSibling)
                      e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : null}
              <div
                className={styles.posterInitials}
                style={{ display: fullPhotoSrc ? "none" : "flex" }}
              >
                {initials || "U"}
              </div>

              <div className={styles.posterMeta}>
                <span className={styles.posterLabel}>Posted By</span>
                <h4 className={styles.posterName}>{creatorName}</h4>
                <span className={styles.posterRole}>
                  {userType || "Community Member"}
                </span>
              </div>
            </div>

            {/* Direct Contact Links */}
            {(mobileNumber || emailAddress) && (
              <div className={styles.contactDetails}>
                {mobileNumber && (
                  <a
                    href={`tel:${mobileNumber}`}
                    className={styles.contactLink}
                  >
                    <FiPhone size={14} className="text-primary" />
                    <span>{mobileNumber}</span>
                  </a>
                )}
                {emailAddress && (
                  <a
                    href={`mailto:${emailAddress}`}
                    className={styles.contactLink}
                  >
                    <FiMail size={14} className="text-primary" />
                    <span>{emailAddress}</span>
                  </a>
                )}
              </div>
            )}

            {/* Meta info: Date and Location */}
            <div className={styles.metaInfoList}>
              {createdOn && (
                <div className={styles.metaInfoItem}>
                  <FiClock size={13} />
                  <span>
                    Posted <TimeAgo timestamp={createdOn} />
                  </span>
                </div>
              )}
              {locationString && (
                <div className={styles.metaInfoItem}>
                  <FiMapPin size={13} />
                  <span>{locationString}</span>
                </div>
              )}
            </div>

            {/* Discussion Button */}
            {discussionPath && (
              <button
                type="button"
                className={styles.sidebarActionBtn}
                onClick={() => navigate(discussionPath)}
              >
                <FiMessageSquare size={16} />
                <span>Open Discussion Page</span>
              </button>
            )}
          </div>

          {/* Quick Info Card */}
          <div className={styles.tipsCard}>
            <h5 className={styles.tipsTitle}>
              <FiInfo size={16} /> Partner Quick Note
            </h5>
            <p className={styles.tipsText}>
              Review the client's requirement specifications above. Click "View
              Discussion" to communicate directly with the client or community
              partners regarding this post.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetailCard;
